import { LoadingButton } from "@mui/lab";
import { Box, FormControl, FormLabel, Grid, TextField, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQuery } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import Breadcrumb from "../../components/Breadcrumb";
import MainLayout from "../../components/MainLayout";
import ProtectedPermissionPage from "../../components/ProtectedPermissionPage";
import useNotification from "../../hooks/useNotification";
import forecastService from "../../services/forecast";
import monthMapping from "../../utils/month";
import queryDefault from "../../utils/queryDefault";

export default function ForecastEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const notification = useNotification();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm({
    defaultValues: {
      roll_count_beginning: 0,
      kertas_count_beginning: 0,
      roll_count_end: 0,
      kertas_count_end: 0,
      used_kertas: 0,
    },
  });

  const { data: forecastData } = useQuery(
    ["forecast-detail", id],
    () => forecastService.viewForecast(String(id)),
    {
      ...queryDefault,
      enabled: Boolean(id),
    }
  );

  useEffect(() => {
    if (forecastData?.data) {
      form.reset({
        roll_count_beginning: forecastData.data.roll_count_beginning ?? 0,
        kertas_count_beginning: forecastData.data.kertas_count_beginning ?? 0,
        roll_count_end: forecastData.data.roll_count_end ?? 0,
        kertas_count_end: forecastData.data.kertas_count_end ?? 0,
        used_kertas: forecastData.data.used_kertas ?? 0,
      });
    }
  }, [form, forecastData]);

  const updateForecast = useMutation(forecastService.updateForecast, {
    onSuccess: () => {
      setIsSubmitting(false);
      navigate("/app/forecast");
      notification.onOpen({
        message: "Forecast updated successfully!",
        type: "success",
        position: "top",
      });
    },
    onError: () => {
      setIsSubmitting(false);
      notification.onOpen({
        message: "Failed to update forecast",
        type: "error",
        position: "top",
      });
    },
    ...queryDefault,
  });

  const onSubmit = (values: any) => {
    if (!id) return;
    setIsSubmitting(true);
    updateForecast.mutate({
      id,
      roll_count_beginning: Number(values.roll_count_beginning) || 0,
      kertas_count_beginning: Number(values.kertas_count_beginning) || 0,
      roll_count_end: Number(values.roll_count_end) || 0,
      kertas_count_end: Number(values.kertas_count_end) || 0,
      used_kertas: Number(values.used_kertas) || 0,
    });
  };

  const forecast = forecastData?.data;

  return (
    <MainLayout>
      <Helmet>
        <title>Edit Forecast</title>
      </Helmet>

      <Breadcrumb
        label="Edit Forecast"
        breadcrumbs={[
          { label: "Dashboard", href: "/app" },
          { label: "Forecast", href: "/app/forecast" },
        ]}
      />

      <ProtectedPermissionPage acceptPermissions={[1, 2]}>
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            Forecast Detail
          </Typography>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item lg={4} xs={12}>
              <TextField fullWidth size="small" label="Region" value={forecast?.region_name || ""} disabled />
            </Grid>
            <Grid item lg={2} xs={6}>
              <TextField fullWidth size="small" label="Month" value={monthMapping[forecast?.month] || ""} disabled />
            </Grid>
            <Grid item lg={2} xs={6}>
              <TextField fullWidth size="small" label="Year" value={forecast?.year || ""} disabled />
            </Grid>
          </Grid>

          <Grid container>
            <Grid item lg={4} xs={12}>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel required>Roll Count Beginning</FormLabel>
                <Controller
                  name="roll_count_beginning"
                  control={form.control}
                  render={({ field }) => (
                    <TextField {...field} type="number" size="small" />
                  )}
                />
              </FormControl>

              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel required>Kertas Count Beginning</FormLabel>
                <Controller
                  name="kertas_count_beginning"
                  control={form.control}
                  render={({ field }) => (
                    <TextField {...field} type="number" size="small" />
                  )}
                />
              </FormControl>

              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel required>Roll Count End</FormLabel>
                <Controller
                  name="roll_count_end"
                  control={form.control}
                  render={({ field }) => (
                    <TextField {...field} type="number" size="small" />
                  )}
                />
              </FormControl>

              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel required>Kertas Count End</FormLabel>
                <Controller
                  name="kertas_count_end"
                  control={form.control}
                  render={({ field }) => (
                    <TextField {...field} type="number" size="small" />
                  )}
                />
              </FormControl>

              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel required>Used Kertas</FormLabel>
                <Controller
                  name="used_kertas"
                  control={form.control}
                  render={({ field }) => (
                    <TextField {...field} type="number" size="small" />
                  )}
                />
              </FormControl>
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ mt: 3 }}>
          <LoadingButton
            variant="contained"
            loading={isSubmitting}
            onClick={form.handleSubmit(onSubmit)}
          >
            Save
          </LoadingButton>
        </Box>
      </ProtectedPermissionPage>
    </MainLayout>
  );
}